import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  NgZone,
  inject,
} from '@angular/core';
import { CoreService } from 'src/app/services/core.service';
import { MatListItem, MatNavList } from '@angular/material/list';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import {
  CONFIG_NAV_MODULE_ID,
  defaultRouteForModule,
  defaultTenantRouteForModule,
  routesForModule,
  findNavModuleIdForUrl,
  isConfigurationUrl,
  isProfileUtilityUrl,
  menuItems as defaultMenuItems,
  navItems as defaultNavItems,
} from '../sidebar-data';
import { MatButtonModule } from '@angular/material/button';
import { filter, Subscription } from 'rxjs';
import { NavItem } from '../nav-item/nav-item';
import { TranslateModule } from '@ngx-translate/core';
import { rolesIncludeSuperadmin } from '@core/auth/superadmin-access';
import { AuthSessionService } from '@core/auth/auth-session.service';
import { ADMIN_SHELL_NAV_ICONS } from '@core/shell/admin-shell-nav-icons';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import {
  PLATFORM_NAV_MODULE_ID,
  POS_RAIL_CARRITOS_ID,
  POS_RAIL_EXIT_ID,
  POS_RAIL_MODULE_ID_SET,
} from '@core/shell/admin-shell-mode.model';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
} from '@angular/cdk/overlay';
import {
  PUBLIC_BRAND_ISOTYPE_BLACK_SRC,
  PUBLIC_BRAND_ISOTYPE_WHITE_SRC,
} from 'src/app/core/branding/public-brand';
import { ToastService } from '@shared/ui/toast/toast.service';
import {
  isPosShellEntryRoute,
  POS_VENTA_ENTRY_ROUTE,
} from '@shared/ui/confirm-open-tab-dialog/confirm-enter-pos';
import { PosOrdersHubNavBadgeService } from '@features/pos/public-api';

/** Ms que el módulo elegido a mano gana frente a sync por URL tras un redirect (p. ej. /profile). */
const RAIL_SELECTION_PIN_MS = 1500;

/**
 * Rail de iconos del admin shell.
 *
 * Highlight (`selectedMenu`) alineado con `selectedModuleId` del `FullComponent`.
 * Ver docs/DEVELOPMENT.md «Admin shell rail sync».
 */
@Component({
  selector: 'app-iconmenu',
  standalone: true,
  templateUrl: './iconmenu.component.html',
  imports: [
    MatListItem,
    MatNavList,
    MatButtonModule,
    TranslateModule,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
  ],
})
export class IconMenuComponent implements OnInit, OnDestroy, OnChanges {
  @Input() menuItems = defaultMenuItems;
  @Input() navItemsForModule = defaultNavItems;
  /**
   * Módulo activo del shell (`FullComponent.selectedIcon`).
   * Fuente de verdad del highlight tras End o rebuild de nav.
   */
  @Input() selectedModuleId: number | null = null;
  /** Solo en escritorio contraído: habilita preview flotante de submódulos. */
  @Input() collapsed = false;

  private readonly settings = inject(CoreService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly authSession = inject(AuthSessionService);
  private readonly adminShellMode = inject(AdminShellModeService);
  private readonly toast = inject(ToastService);
  private readonly hubBadge = inject(PosOrdersHubNavBadgeService);

  readonly posIsotypeBlackSrc = PUBLIC_BRAND_ISOTYPE_BLACK_SRC;
  readonly posIsotypeWhiteSrc = PUBLIC_BRAND_ISOTYPE_WHITE_SRC;
  readonly hubPendingCount = this.hubBadge.pendingCount;
  readonly pedidosRailId = POS_RAIL_CARRITOS_ID;

  options = this.settings.getOptions();
  selectedMenu: number | null = null;
  private navSub = Subscription.EMPTY;
  /** Evita que un NavigationEnd de redirect (p. ej. /profile) pise el módulo recién elegido. */
  private railSelectionPinnedUntil = 0;
  private pinnedModuleId: number | null = null;

  flyoutOpen = false;
  flyoutOrigin: CdkOverlayOrigin | null = null;
  flyoutModuleId: number | null = null;
  flyoutChildren: NavItem[] = [];
  private flyoutCloseTimer: ReturnType<typeof setTimeout> | null = null;

  readonly flyoutPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'top',
      offsetX: 8,
      offsetY: -8,
    },
    {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: 8,
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetX: 8,
      offsetY: 8,
    },
  ];

  @Output() toggleCollapsed = new EventEmitter<void>();
  @Output() openClosedMenu = new EventEmitter<void>();
  @Output() iconSelected = new EventEmitter<number>();
  /** Tras salir del POS con éxito: el shell debe expandir el panel tenant. */
  @Output() posExitCompleted = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['collapsed'] && !this.collapsed) {
      this.closeFlyoutNow();
    }
    if (changes['selectedModuleId'] && !this.isRailSelectionPinned()) {
      this.applySelectedModuleId(this.selectedModuleId);
    }
    if (changes['navItemsForModule'] && !changes['navItemsForModule'].firstChange) {
      this.syncRailFromUrl(this.router.url);
    }
  }

  showContextSwitch(): boolean {
    return (
      rolesIncludeSuperadmin(this.authSession.user()?.roles) &&
      this.adminShellMode.mode() !== 'pos'
    );
  }

  isPosShell(): boolean {
    return this.adminShellMode.mode() === 'pos';
  }

  contextSwitchIcon(): string {
    const preferred =
      this.adminShellMode.mode() === 'platform'
        ? ADMIN_SHELL_NAV_ICONS.switchToMilinco
        : ADMIN_SHELL_NAV_ICONS.switchToPlatform;
    const railIcons = new Set(
      this.getFlattenedItems(this.menuItems).map((item) => item.iconName)
    );
    if (!railIcons.has(preferred)) {
      return preferred;
    }
    const alternates =
      this.adminShellMode.mode() === 'platform'
        ? [
            'solar:shop-2-line-duotone',
            'solar:case-minimalistic-line-duotone',
            'solar:cart-large-2-line-duotone',
          ]
        : [
            'solar:planet-line-duotone',
            'solar:widget-3-line-duotone',
            'solar:buildings-3-line-duotone',
          ];
    return alternates.find((icon) => !railIcons.has(icon)) ?? preferred;
  }

  contextSwitchLabelKey(): string {
    return this.adminShellMode.mode() === 'platform'
      ? 'shell.goToMilinco'
      : 'shell.goToPlatform';
  }

  onContextSwitch(): void {
    this.adminShellMode.toggleContextSwitch();
  }

  getFlattenedItems(
    items: { id: number; title: string; iconName: string; planLocked?: boolean; subItems?: unknown[] }[]
  ): typeof items {
    let result: typeof items = [];
    for (const item of items) {
      result.push(item);
      if (item.subItems?.length) {
        result = result.concat(this.getFlattenedItems(item.subItems as typeof items));
      }
    }
    return result;
  }

  onRailItemEnter(origin: CdkOverlayOrigin, moduleId: number): void {
    if (!this.collapsed || !this.canUseHoverPreview()) {
      return;
    }
    const children = this.childrenForModule(moduleId).filter((child) => !child.navCap && !!child.route);
    if (children.length <= 1) {
      this.closeFlyoutNow();
      return;
    }
    this.clearFlyoutCloseTimer();
    this.flyoutOrigin = origin;
    this.flyoutModuleId = moduleId;
    this.flyoutChildren = this.childrenForModule(moduleId);
    this.flyoutOpen = this.flyoutChildren.length > 0;
  }

  onRailItemLeave(): void {
    this.scheduleFlyoutClose();
  }

  onFlyoutEnter(): void {
    this.clearFlyoutCloseTimer();
  }

  onFlyoutLeave(): void {
    this.scheduleFlyoutClose();
  }

  closeFlyoutFromOutside(): void {
    this.closeFlyoutNow();
  }

  onFlyoutNavigate(moduleId: number): void {
    this.iconSelected.emit(moduleId);
    this.selectedMenu = moduleId;
    this.closeFlyoutNow();
  }

  /** Clic en hija del flyout: POS entra en la pestaña actual; el resto navega por URL. */
  onFlyoutLinkClick(event: MouseEvent, child: NavItem): void {
    event.preventDefault();
    event.stopPropagation();
    if (child.disabled || !child.route?.trim() || this.flyoutModuleId == null) {
      return;
    }
    const moduleId = this.flyoutModuleId;
    this.onFlyoutNavigate(moduleId);
    const url = child.route.startsWith('/') ? child.route : `/${child.route}`;
    this.ngZone.run(() => {
      if (isPosShellEntryRoute(url)) {
        this.adminShellMode.enterPos(url === '/pos' ? POS_VENTA_ENTRY_ROUTE : url);
        return;
      }
      void this.router.navigateByUrl(url).then((ok) => {
        this.handleModuleNavigationResult(moduleId, url, ok);
      });
    });
  }

  isRouteActive(route: string | undefined): boolean {
    if (!route) {
      return false;
    }
    return this.router.isActive(route, {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  trackFlyoutChild(item: NavItem, index: number): string {
    return `${item.route ?? item.navCap ?? item.displayName ?? ''}_${index}`;
  }

  selectIcon(icon: number): void {
    this.closeFlyoutNow();

    // Salida POS → Milinco: no pin ni selectedIcon=1407 (deja el panel tenant vacío).
    if (icon === POS_RAIL_EXIT_ID) {
      this.clearRailSelectionPin();
      this.ngZone.run(() => {
        void this.adminShellMode.exitPos().then((ok) => {
          if (!ok) {
            this.resyncHighlightFromShell();
            this.toast.infoTranslated('pos.shell.exitCancelled');
            return;
          }
          this.clearRailSelectionPin();
          this.syncRailFromUrl(this.router.url);
          this.posExitCompleted.emit();
        });
      });
      return;
    }

    this.pinRailSelection(icon);
    this.iconSelected.emit(icon);
    this.selectedMenu = icon === CONFIG_NAV_MODULE_ID ? null : icon;

    const currentPath = this.router.url.split('?')[0];
    const currentModuleId = findNavModuleIdForUrl(this.navItemsForModule, currentPath);

    if (currentModuleId === icon) {
      const hubRoute = this.hubRouteForModule(icon);
      if (
        hubRoute &&
        currentPath !== hubRoute &&
        currentPath.startsWith(`${hubRoute}/`)
      ) {
        this.navigateKeepingModule(icon, hubRoute);
        return;
      }
      // Módulo hoja (Inicio): sin submenú que abrir/cerrar.
      if (routesForModule(this.navItemsForModule, icon).length <= 1) {
        return;
      }
      this.toggleCollapsed.emit();
      return;
    }

    // Tenant: primera hija no-/pos/*. POS shell: primera hija del módulo (sí /pos/*).
    const route = this.hubRouteForModule(icon);
    // Inicio (u hoja) deja el shell en mini vía hideModuleSubnav; al pasar a un módulo
    // con hijos hay que expandir el panel o el usuario necesita un segundo clic.
    if (routesForModule(this.navItemsForModule, icon).length > 1) {
      this.openClosedMenu.emit();
    }
    if (route) {
      this.navigateKeepingModule(icon, route);
    }
  }

  private hubRouteForModule(moduleId: number): string | null {
    if (this.isPosShell() || POS_RAIL_MODULE_ID_SET.has(moduleId)) {
      return defaultRouteForModule(this.navItemsForModule, moduleId);
    }
    return defaultTenantRouteForModule(this.navItemsForModule, moduleId);
  }

  /**
   * Navega a una hija. Si canDeactivate (u otro guard) cancela, revierte highlight a la URL.
   * Si un guard redirige fuera del target (p. ej. `/profile`), conserva el módulo elegido
   * para que el usuario pueda probar otra hija (regla 4 del rail sync).
   */
  private navigateKeepingModule(moduleId: number, route: string): void {
    this.ngZone.run(() => {
      void this.router.navigateByUrl(route).then((ok) => {
        this.handleModuleNavigationResult(moduleId, route, ok);
      });
    });
  }

  private handleModuleNavigationResult(
    moduleId: number,
    route: string,
    ok: boolean
  ): void {
    const landed = this.router.url.split('?')[0];
    const target = route.split('?')[0];
    const onTarget = landed === target || landed.startsWith(`${target}/`);

    if (ok && onTarget) {
      this.ensureModuleSelection(moduleId);
      return;
    }

    if (!ok) {
      // Cancelación: URL intacta (p. ej. descartar cambios → Cancelar).
      this.revertRailSelectionToUrl();
      return;
    }

    // Redirect UrlTree u otro aterrizaje fuera del target.
    this.ensureModuleSelection(moduleId);
    this.toast.errorTranslated('sidebar.navFailed');
  }

  private revertRailSelectionToUrl(): void {
    this.clearRailSelectionPin();
    this.syncRailFromUrl(this.router.url);
  }

  private ensureModuleSelection(moduleId: number): void {
    this.pinRailSelection(moduleId);
    this.applySelectedModuleId(moduleId);
    this.iconSelected.emit(moduleId);
  }

  private pinRailSelection(moduleId: number): void {
    if (moduleId === POS_RAIL_EXIT_ID || POS_RAIL_MODULE_ID_SET.has(moduleId)) {
      return;
    }
    this.pinnedModuleId = moduleId;
    this.railSelectionPinnedUntil = Date.now() + RAIL_SELECTION_PIN_MS;
  }

  private clearRailSelectionPin(): void {
    this.pinnedModuleId = null;
    this.railSelectionPinnedUntil = 0;
  }

  private isRailSelectionPinned(): boolean {
    return Date.now() < this.railSelectionPinnedUntil && this.pinnedModuleId != null;
  }

  private childrenForModule(moduleId: number): NavItem[] {
    const module = this.navItemsForModule.find((item) => item.id === moduleId);
    return module?.children ?? [];
  }

  private canUseHoverPreview(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  private scheduleFlyoutClose(): void {
    this.clearFlyoutCloseTimer();
    this.flyoutCloseTimer = setTimeout(() => this.closeFlyoutNow(), 180);
  }

  private clearFlyoutCloseTimer(): void {
    if (this.flyoutCloseTimer != null) {
      clearTimeout(this.flyoutCloseTimer);
      this.flyoutCloseTimer = null;
    }
  }

  private closeFlyoutNow(): void {
    this.clearFlyoutCloseTimer();
    this.flyoutOpen = false;
    this.flyoutModuleId = null;
    this.flyoutChildren = [];
  }

  private resyncHighlightFromShell(): void {
    this.syncRailFromUrl(this.router.url);
  }

  private applySelectedModuleId(id: number | null | undefined): void {
    if (id === CONFIG_NAV_MODULE_ID || id == null) {
      this.selectedMenu = null;
      return;
    }
    this.selectedMenu = id;
  }

  private syncRailFromUrl(url: string, emitSelection = true): void {
    if (this.isRailSelectionPinned() && this.pinnedModuleId != null) {
      this.applySelectedModuleId(this.pinnedModuleId);
      if (emitSelection) {
        this.iconSelected.emit(this.pinnedModuleId);
      }
      return;
    }

    const path = url.split('?')[0];
    if (path.startsWith('/platform')) {
      this.selectedMenu = PLATFORM_NAV_MODULE_ID;
      if (emitSelection) {
        this.iconSelected.emit(PLATFORM_NAV_MODULE_ID);
      }
      return;
    }

    if (isProfileUtilityUrl(path)) {
      this.selectedMenu = null;
      return;
    }

    const id = findNavModuleIdForUrl(this.navItemsForModule, path);
    if (id === CONFIG_NAV_MODULE_ID || isConfigurationUrl(path)) {
      this.selectedMenu = null;
      if (emitSelection) {
        this.iconSelected.emit(CONFIG_NAV_MODULE_ID);
      }
      return;
    }

    if (id != null) {
      this.selectedMenu = id;
      if (emitSelection) {
        this.iconSelected.emit(id);
      }
      return;
    }

    this.applySelectedModuleId(this.selectedModuleId);
  }

  ngOnInit(): void {
    this.applySelectedModuleId(this.selectedModuleId);
    this.syncRailFromUrl(this.router.url, false);
    this.navSub = this.router.events
      .pipe(
        filter(
          (
            e
          ): e is NavigationEnd | NavigationCancel | NavigationError =>
            e instanceof NavigationEnd ||
            e instanceof NavigationCancel ||
            e instanceof NavigationError
        )
      )
      .subscribe((e) => {
        if (e instanceof NavigationEnd) {
          this.syncRailFromUrl(e.urlAfterRedirects);
          this.closeFlyoutNow();
          return;
        }
        // canDeactivate cancelado / error: soltar pin y realinear con la URL actual.
        this.revertRailSelectionToUrl();
        this.closeFlyoutNow();
      });
  }

  ngOnDestroy(): void {
    this.closeFlyoutNow();
    this.navSub.unsubscribe();
  }
}
